import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { faTowerBroadcast } from '@fortawesome/pro-light-svg-icons/faTowerBroadcast';
import { TypedMemo } from '../../common/types';
import { useGetPromoChannelsQuery, useGetPromoPostsQuery, useGetReelsQuery } from '../../../redux/api/fymAPI';
import { useMusic24Capabilities } from '../hooks/useMusic24Capabilities';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { aiReelStudioParams } from '../promote/studioRoute';
import { HomeBlock } from './HomeBlock';
import { HomeBlockSkeleton } from './HomeBlockSkeleton';
import { HomeSetupCard } from './HomeSetupCard';
import { PromoteNextPosts } from './PromoteNextPosts';
import { PromoteNeedsYou } from './PromoteNeedsYou';
import { PromoteLatestReels } from './PromoteLatestReels';
import { artistHomeMessages as messages } from './artistHomeMessages';
import {
  PROMOTE_LIST_ROWS,
  channelsNeedingReconnect,
  failedPosts,
  hasAnyChannel,
  latestReels,
  promotePostsWindow,
  upcomingPosts,
} from './promoteSummary';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PromoteBlockProps {
  accountId: number;
  /**
   * True when the Social block shows "Connect your channels". The Promote block with no channel
   * then shows nothing, so Home has one connect card (design music24-social-connectors d2).
   */
  isConnectCardHidden?: boolean;
}

/**
 * The Promote block (design a1): the next scheduled posts, failed posts, channels that need a new
 * sign-in and the latest reels (only with `canUseAiReels`). With no channel connected it shows
 * "Connect a channel" (design a2), or nothing when the Social block shows its connect card.
 */
const PromoteBlockComponent: React.FC<PromoteBlockProps> = ({ accountId, isConnectCardHidden = false }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { canUseAiReels } = useMusic24Capabilities();
  // Set once per mount, so the query key does not move with the clock.
  const now = useMemo(() => new Date(), []);
  const postsWindow = useMemo(() => promotePostsWindow(now), [now]);

  const channels = useGetPromoChannelsQuery({ accountId });
  const posts = useGetPromoPostsQuery({ accountId, ...postsWindow });
  const reels = useGetReelsQuery({ accountId }, { skip: !canUseAiReels });

  const summary = useMemo(() => {
    const allPosts = posts.data?.posts ?? [];
    return {
      next: upcomingPosts(allPosts, now).slice(0, PROMOTE_LIST_ROWS),
      failed: failedPosts(allPosts),
      reconnect: channelsNeedingReconnect(channels.data?.channels ?? []),
      hasChannel: hasAnyChannel(channels.data?.channels ?? []) || allPosts.length > 0,
      reels: latestReels(reels.data?.reels ?? []),
    };
  }, [channels.data, now, posts.data, reels.data]);

  const retry = useCallback(() => {
    channels.refetch().catch(console.error);
    posts.refetch().catch(console.error);
  }, [channels, posts]);
  const openCalendar = useCallback(() => router.push(SMLRoutes.promoteCalendar), [router]);
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const openStudio = useCallback(() => router.push(SMLRoutes.promoteStudio, aiReelStudioParams()), [router]);
  const openReel = useCallback(
    (reel: string) => router.push(SMLRoutes.promoteStudio, aiReelStudioParams({ reel })),
    [router]
  );

  const title = intl.formatMessage(messages.promoteTitle);

  if (channels.isLoading || posts.isLoading) {
    return (
      <HomeBlock icon={faBolt} title={title}>
        <HomeBlockSkeleton />
      </HomeBlock>
    );
  }

  const error = channels.error ?? posts.error;
  // The connect card is the only content of the block with no channel. A failed read still shows.
  if (isConnectCardHidden && !summary.hasChannel && error === undefined) {
    return null;
  }

  return (
    <HomeBlock
      icon={faBolt}
      title={title}
      moreLabel={summary.hasChannel ? intl.formatMessage(messages.promoteOpen) : undefined}
      onMore={openCalendar}
      error={error}
      retry={retry}
    >
      {summary.hasChannel ? (
        <div className={styles.promoGrid}>
          <PromoteNextPosts posts={summary.next} />
          <PromoteNeedsYou
            failed={summary.failed}
            reconnect={summary.reconnect}
            onReview={openCalendar}
            onReconnect={openChannels}
          />
          {canUseAiReels && (
            <PromoteLatestReels reels={summary.reels} onOpenStudio={openStudio} onOpenReel={openReel} />
          )}
        </div>
      ) : (
        <HomeSetupCard
          icon={faTowerBroadcast}
          title={intl.formatMessage(messages.promoteEmptyTitle)}
          body={intl.formatMessage(messages.promoteEmptyBody)}
          cta={intl.formatMessage(messages.promoteEmptyCta)}
          onAction={openChannels}
          isBare
        />
      )}
    </HomeBlock>
  );
};

export const PromoteBlock = TypedMemo(PromoteBlockComponent);
